/**
 * @file Navigation.tsx
 * @description Two-level navigation system:
 *   - Top utility bar: bike/snow switch, theme
 *   - Main navigation: map, list, ranking, content pages
 * @module components/Navigation
 *
 * ARCHITECTURE:
 * ┌─────────────────────────────────────────────────┐
 * │ UTILITY BAR (subtle, secondary)                 │
 * │ Bike/Snow | Theme                               │
 * └─────────────────────────────────────────────────┘
 * ┌─────────────────────────────────────────────────┐
 * │ MAIN NAVIGATION (prominent, primary)            │
 * │ Mapa | Lista | Ranking | Przewodnik ...        │
 * └─────────────────────────────────────────────────┘
 */

'use client';

import { Link } from '@/i18n/routing';
import { usePathname } from '@/i18n/routing';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useLocale as useNextIntlLocale } from 'next-intl';
import LanguageSwitcher from './LanguageSwitcher';
import GlobalSearchWrapper from './GlobalSearchWrapper';
import { articlesData } from '@/app/[locale]/przewodnik/[slug]/articles-data';
import {
  Bike,
  Snowflake,
  Heart,
  ShoppingCart,
  Zap,
  Shield,
  Menu,
  X,
  Home,
  Map,
  List,
  Trophy,
  MapPin,
  DollarSign,
  BookOpen,
  Newspaper,
  Route,
  Flag,
  Wind,
  Plane,
  Mail,
  Rocket,
  ChevronDown
} from 'lucide-react';

export default function Navigation() {
  const t = useTranslations('nav');
  const pathname = usePathname();
  const locale = useNextIntlLocale();

  // Detect domain (client-side)
  const [isBikeMapka, setIsBikeMapka] = useState(true);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bikeparkiDropdownOpen, setBikeparkiDropdownOpen] = useState(false);
  const [trasyDropdownOpen, setTrasyDropdownOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [emtbDropdownOpen, setEmtbDropdownOpen] = useState(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);
    const domainOverride = searchParams.get('domain');

    // Detect if we're on localhost
    setIsLocalhost(hostname === 'localhost' || hostname === '127.0.0.1');

    // Allow localhost testing with ?domain=snowpark
    if (domainOverride === 'snowpark') {
      setIsBikeMapka(false);
    } else if (domainOverride === 'bikepark') {
      setIsBikeMapka(true);
    } else {
      setIsBikeMapka(hostname.includes('bikeparkmapa.pl') || hostname.includes('localhost'));
    }
  }, []);

  return (
    <>
      {/* UTILITY BAR - Top secondary navigation */}
      <div className="bp-utility-bar">
        <div className="bp-utility-container">
          {/* Logo */}
          <Link
            href="/"
            className="bp-utility-logo"
          >
            {isBikeMapka ? (
              <>
                <Bike size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '6px'}} />
                BikeParkMapa
              </>
            ) : (
              <>
                <Snowflake size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '6px'}} />
                SnowParkMap
              </>
            )}
          </Link>

          {/* Global Search */}
          <div className="bp-utility-search">
            <GlobalSearchWrapper />
          </div>

          <div className="bp-utility-spacer"></div>

          {/* Mobile menu toggle */}
          <button
            className="bp-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Favorites */}
          <div className="bp-utility-item">
            <Link href="/ulubione" className="bp-favorites-link" title="Ulubione">
              <Heart size={18} strokeWidth={2} />
            </Link>
          </div>

          {/* Language */}
          <div className="bp-utility-item">
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* MAIN NAVIGATION - Primary content navigation */}
      <nav className={`bp-main-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="bp-main-nav-container">
          {/* BikeParkMapa navigation */}
          {isBikeMapka && (
            <>
              <Link
                href="/"
                className={`bp-main-nav-link ${pathname === '/' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('home')}
              </Link>

              {/* Mapa Bikeparków - GŁÓWNY LINK */}
              <Link
                href="/bikeparki"
                className="bp-main-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Map size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('map')}
              </Link>

              {/* Bikeparki Dropdown - Desktop only */}
              <div
                className="bp-nav-dropdown bp-desktop-only"
                onMouseEnter={() => setBikeparkiDropdownOpen(true)}
                onMouseLeave={() => setBikeparkiDropdownOpen(false)}
              >
                <button className="bp-main-nav-link bp-dropdown-trigger">
                  <Bike size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                  {t('bikeparks')} <ChevronDown size={14} style={{display: 'inline', verticalAlign: 'middle', marginLeft: '2px'}} />
                </button>
                {bikeparkiDropdownOpen && (
                  <div className="bp-dropdown-menu">
                    <Link href="/bikeparki/lista" className="bp-dropdown-item">
                      <List size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('list')}
                    </Link>
                    <Link href="/bikeparki/ranking" className="bp-dropdown-item">
                      <Trophy size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('ranking')}
                    </Link>
                    <Link href="/regiony" className="bp-dropdown-item">
                      <MapPin size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('regions')}
                    </Link>
                    <Link href="/porownanie-cen" className="bp-dropdown-item">
                      <DollarSign size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('prices')}
                    </Link>
                  </div>
                )}
              </div>
              {/* Organize Trip - hide for Polish locale */}
              {locale !== 'pl' && (
                <Link
                  href="/organize-trip"
                  className={`bp-main-nav-link ${pathname === '/organize-trip' ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Plane size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                  {t('organizeTrip')}
                </Link>
              )}
              {/* Trasy Dropdown - Desktop only */}
              <div
                className="bp-nav-dropdown bp-desktop-only"
                onMouseEnter={() => setTrasyDropdownOpen(true)}
                onMouseLeave={() => setTrasyDropdownOpen(false)}
              >
                <button className="bp-main-nav-link bp-dropdown-trigger">
                  <Route size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                  {t('trails')} <ChevronDown size={14} style={{display: 'inline', verticalAlign: 'middle', marginLeft: '2px'}} />
                </button>
                {trasyDropdownOpen && (
                  <div className="bp-dropdown-menu">
                    <Link href="/trasy-downhill" className="bp-dropdown-item">
                      <Flag size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('trailsDownhill')}
                    </Link>
                    <Link href="/trasy-flow" className="bp-dropdown-item">
                      <Wind size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('trailsFlow')}
                    </Link>
                    <Link href="/trasy-air" className="bp-dropdown-item">
                      <Plane size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('trailsAir')}
                    </Link>
                  </div>
                )}
              </div>
              {/* Shop - hide for all locales for now */}
              {false && (
                <Link
                  href="/sklep"
                  className={`bp-main-nav-link bp-desktop-only ${pathname === '/sklep' || pathname.startsWith('/sklep/') ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShoppingCart size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '6px'}} />
                  {t('shop')}
                </Link>
              )}

              {/* Blog Dropdown - Desktop only */}
              <div
                className="bp-nav-dropdown bp-desktop-only"
                onMouseEnter={() => setBlogDropdownOpen(true)}
                onMouseLeave={() => setBlogDropdownOpen(false)}
              >
                <button className="bp-main-nav-link bp-dropdown-trigger">
                  <BookOpen size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                  {t('blog')} <ChevronDown size={14} style={{display: 'inline', verticalAlign: 'middle', marginLeft: '2px'}} />
                </button>
                {blogDropdownOpen && (
                  <div className="bp-dropdown-menu">
                    <Link href="/blog" className="bp-dropdown-item">
                      <Newspaper size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('blogPosts')}
                    </Link>
                    <Link href="/przewodnik" className="bp-dropdown-item">
                      <BookOpen size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('guide')}
                    </Link>
                    <div className="bp-dropdown-divider"></div>

                    {/* E-MTB Tuning nested dropdown */}
                    <div
                      className="bp-dropdown-nested"
                      onMouseEnter={() => setEmtbDropdownOpen(true)}
                      onMouseLeave={() => setEmtbDropdownOpen(false)}
                    >
                      <button className="bp-dropdown-item bp-dropdown-nested-trigger">
                        <Zap size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                        {t('emtbTuning')} <span className="dropdown-arrow-right">›</span>
                      </button>
                      {emtbDropdownOpen && (
                        <div className="bp-dropdown-submenu">
                          <Link href={`/przewodnik/${articlesData['speedbox-review'].slugs[locale] || articlesData['speedbox-review'].slugs.pl}`} className="bp-dropdown-item">
                            {articlesData['speedbox-review'].titles[locale]?.split(' - ')[0] || 'SpeedBox 3.0 Bosch'}
                          </Link>
                          <Link href={`/przewodnik/${articlesData['top-5-chips'].slugs[locale] || articlesData['top-5-chips'].slugs.pl}`} className="bp-dropdown-item">
                            {articlesData['top-5-chips'].titles[locale]?.split(' - ')[0] || 'Top 5 Tuning Chips'}
                          </Link>
                          <Link href={`/przewodnik/${articlesData['tuning-worth-it'].slugs[locale] || articlesData['tuning-worth-it'].slugs.pl}`} className="bp-dropdown-item">
                            {articlesData['tuning-worth-it'].titles[locale]?.split(' - ')[0] || 'E-bike Tuning'}
                          </Link>
                        </div>
                      )}
                    </div>

                    <div className="bp-dropdown-divider"></div>
                    <Link href="/ubezpieczenie-rowerowe" className="bp-dropdown-item">
                      <Shield size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                      {t('insurance')}
                    </Link>
                    <Link href="/jak-zaczac" className="bp-dropdown-item">
                      <Rocket size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('gettingStarted')}
                    </Link>
                    <Link href="/sprzet" className="bp-dropdown-item">
                      <ShoppingCart size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '6px'}} />
                      {t('gear')}
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile menu items */}
              <span className="bp-main-nav-label bp-mobile-only"><Bike size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />{t('bikeparks')}:</span>
              <Link
                href="/bikeparki/lista"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/bikeparki/lista' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <List size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('list')}
              </Link>
              <Link
                href="/bikeparki/ranking"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/bikeparki/ranking' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('ranking')}
              </Link>
              <Link
                href="/porownanie-cen"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/porownanie-cen' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <DollarSign size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('prices')}
              </Link>

              <span className="bp-main-nav-label bp-mobile-only"><Route size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />{t('trails')}:</span>
              <Link
                href="/trasy-downhill"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/trasy-downhill' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Flag size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('trailsDownhill')}
              </Link>
              <Link
                href="/trasy-flow"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/trasy-flow' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Wind size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('trailsFlow')}
              </Link>
              <Link
                href="/trasy-air"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/trasy-air' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Plane size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('trailsAir')}
              </Link>

              {/* Shop - hide for all locales for now - mobile */}
              {false && (
                <Link
                  href="/sklep"
                  className={`bp-main-nav-link bp-mobile-only ${pathname === '/sklep' || pathname.startsWith('/sklep/') ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShoppingCart size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '6px'}} />
                  {t('shop')}
                </Link>
              )}

              <span className="bp-main-nav-label bp-mobile-only"><BookOpen size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />{t('blog')}:</span>
              <Link
                href="/blog"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/blog' || pathname.startsWith('/blog/') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Newspaper size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('blogPosts')}
              </Link>
              <Link
                href="/przewodnik"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/przewodnik' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('guide')}
              </Link>
              <Link
                href="/ubezpieczenie-rowerowe"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/ubezpieczenie-rowerowe' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Shield size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '6px'}} />
                {t('insurance')}
              </Link>
              <Link
                href="/jak-zaczac"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/jak-zaczac' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Rocket size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('gettingStarted')}
              </Link>
              <Link
                href="/sprzet"
                className={`bp-main-nav-link bp-mobile-only ${pathname === '/sprzet' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingCart size={14} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('gear')}
              </Link>

              {/* Kontakt - last item in navigation */}
              <Link
                href="/kontakt"
                className={`bp-main-nav-link ${pathname === '/kontakt' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Mail size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('contact')}
              </Link>
            </>
          )}

          {/* SnowParkMap navigation */}
          {!isBikeMapka && (
            <>
              <Link
                href="/"
                className={`bp-main-nav-link ${pathname === '/' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('home')}
              </Link>
              <Link
                href="/snowparks"
                className={`bp-main-nav-link ${pathname === '/snowparks' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Map size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('map')}
              </Link>
              <Link
                href="/snowparks/list"
                className={`bp-main-nav-link ${pathname === '/snowparks/list' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <List size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('list')}
              </Link>
              <Link
                href="/snowparks/ranking"
                className={`bp-main-nav-link ${pathname === '/snowparks/ranking' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('ranking')}
              </Link>

              <div className="bp-main-nav-divider"></div>

              <Link
                href="/przewodnik"
                className={`bp-main-nav-link ${pathname === '/przewodnik' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('guide')}
              </Link>
              <Link
                href="/jak-zaczac"
                className={`bp-main-nav-link ${pathname === '/jak-zaczac' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Rocket size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('gettingStarted')}
              </Link>
              <Link
                href="/sprzet"
                className={`bp-main-nav-link ${pathname === '/sprzet' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingCart size={16} style={{display: 'inline', verticalAlign: 'middle', marginRight: '4px'}} />
                {t('gear')}
              </Link>
            </>
          )}
        </div>
      </nav>
    </>
  );
}
